import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  PasswordInput,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";

export function AccountPage() {
  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Change Password"
        description="Update the password for your Phoenix Capital sign-in."
      />
      <div className="max-w-[640px]">
        <ChangePasswordCard />
      </div>
    </>
  );
}

/** Also rendered by the forced-change gate (`ForcePasswordChangePage`) —
 *  `onChanged` lets that surface refresh the auth session after success. */
export function ChangePasswordCard({ onChanged }: { onChanged?: () => void } = {}) {
  const { toast } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [newError, setNewError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setCurrentError(null);
    setNewError(null);
    setConfirmError(null);
  };

  const mutation = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      reset();
      toast({ tone: "success", message: "Password changed." });
      onChanged?.();
    },
    onError: (err: Error) => {
      // Identity returns the policy violations as 400 with the offending
      // descriptions in `errors`. A wrong current password comes back as
      // either 400 (PasswordMismatch) or as the catch-all message.
      const errors = err instanceof ApiError ? err.errors : [];
      const isCurrentMismatch = errors.some((e) => /incorrect|mismatch/i.test(e));
      if (isCurrentMismatch || /current/i.test(err.message)) {
        setCurrentError("Current password is incorrect.");
        return;
      }
      setNewError(errors.length ? errors.join(" ") : err.message);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentError(null);
    setNewError(null);
    setConfirmError(null);

    if (next !== confirm) {
      setConfirmError("Passwords do not match.");
      return;
    }
    mutation.mutate();
  };

  const canSave =
    !mutation.isPending && current.length > 0 && next.length > 0 && confirm.length > 0;

  return (
    <Card featured>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
        <CardDescription>
          Choose a strong password — at least 12 characters with upper and lower
          case, a digit, and a non-alphanumeric character.
        </CardDescription>
      </CardHeader>
      <form className="grid gap-space-3" onSubmit={onSubmit}>
        <FormField
          label="Current password"
          required
          htmlFor="account-current"
          error={currentError ?? undefined}
        >
          <PasswordInput
            id="account-current"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            invalid={!!currentError}
            required
          />
        </FormField>
        <FormField
          label="New password"
          required
          htmlFor="account-new"
          error={newError ?? undefined}
        >
          <PasswordInput
            id="account-new"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            invalid={!!newError}
            required
          />
        </FormField>
        <FormField
          label="Confirm new password"
          required
          htmlFor="account-confirm"
          error={confirmError ?? undefined}
        >
          <PasswordInput
            id="account-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            invalid={!!confirmError}
            required
          />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" loading={mutation.isPending} disabled={!canSave}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}
